import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldWarning, Prohibit, Warning, Clock, TrendDown, ArrowRight, ChartBar, Recycle, Lightning } from "@phosphor-icons/react";

function Kpi({ label, value, suffix, tone, testid }) {
  return (
    <Card className="rounded-md border p-4" data-testid={testid}>
      <div className={`text-3xl font-black tabular-nums tracking-tight ${tone || ""}`}>{value ?? "—"}{value != null && suffix ? suffix : ""}</div>
      <div className="mt-1"><Label2>{label}</Label2></div>
    </Card>
  );
}

function AssetRow({ a, right, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex w-full items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2 text-left transition-colors hover:bg-muted/50">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{a.name}</div>
        <div className="text-[11px] text-muted-foreground">{a.asset_type}</div>
      </div>
      <div className="flex items-center gap-2">{right}<ArrowRight size={14} className="text-muted-foreground" /></div>
    </button>
  );
}

export default function Kennzahlen() {
  const [safety, setSafety] = useState(null);
  const [kpi, setKpi] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/v1/overview/safety").then((r) => setSafety(r.data)).catch(() => setSafety(false));
    api.get("/v1/kpi/summary").then((r) => setKpi(r.data)).catch(() => setKpi(false));
  }, []);

  const go = (id) => nav(`/anlagen/${id}`);

  return (
    <div className="space-y-8" data-testid="kennzahlen-page">
      <PageHeader title="Kennzahlen & Sicherheitsübersicht" subtitle="Akuter Handlungsbedarf und Nutzungskennzahlen auf einen Blick (Sprint 1)" />

      {/* Story 1 — Sicherheitsübersicht */}
      <section className="space-y-4" data-testid="safety-overview">
        <div className="flex items-center gap-2"><ShieldWarning size={20} weight="duotone" className="text-red-500" /><h2 className="font-heading text-lg font-black">Sicherheitsübersicht</h2></div>
        {safety === null ? <Skeleton className="h-40 rounded-md" /> : safety === false ? <p className="text-sm text-muted-foreground">Konnte nicht geladen werden.</p> : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi label="Gesperrte Anlagen" value={safety.gesperrte_anlagen.anzahl} tone="text-red-600" testid="safety-blocked" />
              <Kpi label="Anlagen mit Sicherheitsmängeln" value={safety.offene_sicherheitsmaengel.anzahl} tone="text-amber-600" testid="safety-defect-assets" />
              <Kpi label="Überfällige Wartungen" value={safety.ueberfaellige_wartungen.anzahl} tone="text-amber-600" testid="safety-overdue" />
              <Kpi label="Kritische Mängel (offen)" value={safety.kritische_maengel_nach_prioritaet.kritisch} tone="text-red-600" testid="safety-critical" />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="rounded-md border p-4">
                <div className="mb-2 flex items-center gap-2"><Prohibit size={16} className="text-red-500" /><Label2>Gesperrte Anlagen</Label2></div>
                <div className="space-y-2">
                  {safety.gesperrte_anlagen.anlagen.length === 0 ? <p className="text-xs text-muted-foreground">Keine.</p> :
                    safety.gesperrte_anlagen.anlagen.map((a) => <AssetRow key={a.id} a={a} onClick={() => go(a.id)} testid={`safety-blocked-${a.id}`} right={<Badge variant="outline" className="rounded-md border-red-500/40 text-[10px] text-red-600">gesperrt</Badge>} />)}
                </div>
              </Card>
              <Card className="rounded-md border p-4">
                <div className="mb-2 flex items-center gap-2"><Warning size={16} className="text-amber-500" /><Label2>Offene Sicherheitsmängel</Label2></div>
                <div className="space-y-2">
                  {safety.offene_sicherheitsmaengel.anlagen.length === 0 ? <p className="text-xs text-muted-foreground">Keine.</p> :
                    safety.offene_sicherheitsmaengel.anlagen.map((a) => <AssetRow key={a.id} a={a} onClick={() => go(a.id)} testid={`safety-sdef-${a.id}`} right={<Badge variant="outline" className="rounded-md border-amber-500/40 text-[10px] text-amber-600">{a.anzahl} Mangel</Badge>} />)}
                </div>
              </Card>
            </div>

            <Card className="rounded-md border p-4">
              <div className="mb-2 flex items-center gap-2"><TrendDown size={16} className="text-primary" /><Label2>Health-v2-Abweichungen (|v1−v2| ≥ 15)</Label2></div>
              <div className="space-y-2">
                {safety.health_v2_abweichungen.length === 0 ? <p className="text-xs text-muted-foreground">Keine relevanten Abweichungen.</p> :
                  safety.health_v2_abweichungen.map((r) => <AssetRow key={r.id} a={r} onClick={() => go(r.id)} testid={`safety-dev-${r.id}`}
                    right={<span className="text-xs tabular-nums text-muted-foreground">v1 {r.v1}% · v2 {r.v2}% · <b className={r.diff < 0 ? "text-red-600" : "text-emerald-600"}>{r.diff > 0 ? "+" : ""}{r.diff}</b></span>} />)}
              </div>
            </Card>
          </>
        )}
      </section>

      {/* Story 2 — KPI-Dashboard */}
      <section className="space-y-4" data-testid="kpi-dashboard">
        <div className="flex items-center gap-2"><ChartBar size={20} weight="duotone" className="text-primary" /><h2 className="font-heading text-lg font-black">Management-Kennzahlen</h2></div>
        {kpi === null ? <Skeleton className="h-40 rounded-md" /> : kpi === false ? <p className="text-sm text-muted-foreground">Konnte nicht geladen werden.</p> : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
              <Kpi label="Durchgeführte Wartungen" value={kpi.durchgefuehrte_wartungen} testid="kpi-wartungen" />
              <Kpi label="Offene Mängel" value={kpi.offene_maengel} tone="text-amber-600" testid="kpi-defects" />
              <Kpi label="Offene Sicherheitsmängel" value={kpi.offene_sicherheitsmaengel} tone="text-red-600" testid="kpi-sdefects" />
              <Kpi label="Gesperrte Anlagen" value={kpi.gesperrte_anlagen} tone="text-red-600" testid="kpi-blocked" />
              <Kpi label="Ø Health v1" value={kpi.avg_health_v1} suffix="%" testid="kpi-hv1" />
              <Kpi label="Ø Health v2" value={kpi.avg_health_v2} suffix="%" testid="kpi-hv2" />
              <Kpi label="Ø Differenz v1↔v2" value={kpi.avg_health_differenz} testid="kpi-hdiff" />
              <Kpi label="Ø Zeit Mangel→Reparatur" value={kpi.avg_zeit_mangel_bis_reparatur_tage} suffix=" Tage" testid="kpi-repairtime" />
              <Kpi label="CMS-Core Wiederverwendung" value={kpi.cms_core_wiederverwendungsgrad_prozent} suffix="%" tone="text-emerald-600" testid="kpi-reuse" />
              <Kpi label="Business Rules ausgelöst" value={kpi.business_rules_ausgeloest} tone="text-primary" testid="kpi-br" />
              <Kpi label="Anlagen gesamt" value={kpi.anlagen_gesamt} testid="kpi-assets" />
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lightning size={13} /> {kpi.hinweis_filter}</p>
          </>
        )}
      </section>
    </div>
  );
}
